import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowLeft, ChatCircleDots, Robot, User, CheckCircle, CalendarBlank, Phone,
  EnvelopeSimple, Spinner, ArrowsClockwise,
} from "@phosphor-icons/react";

import { adminClient, getAdminToken } from "@/lib/adminApi";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return iso;
  }
};

function Bubble({ role, text, ts, suggestBooking }) {
  const isBot = role !== "user";
  return (
    <div
      data-testid={`transcript-msg-${role}`}
      className={`flex ${isBot ? "justify-start" : "justify-end"} gap-2`}
    >
      {isBot && (
        <div className="h-8 w-8 rounded-full bg-blue-600/20 text-blue-300 grid place-items-center shrink-0">
          <Robot size={16} weight="duotone" />
        </div>
      )}
      <div className={`max-w-[80%] flex flex-col ${isBot ? "items-start" : "items-end"}`}>
        <div
          className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
            isBot
              ? "bg-zinc-800 text-zinc-100 rounded-bl-md"
              : "bg-blue-600 text-white rounded-br-md"
          }`}
        >
          {text}
        </div>
        <div className="mt-1 text-[11px] text-zinc-500 flex items-center gap-1.5">
          <span>{fmtDate(ts)}</span>
          {suggestBooking && (
            <span className="inline-flex items-center gap-1 text-blue-400">
              · <CalendarBlank size={11} weight="bold" /> booking suggested
            </span>
          )}
        </div>
      </div>
      {!isBot && (
        <div className="h-8 w-8 rounded-full bg-zinc-700 text-zinc-200 grid place-items-center shrink-0">
          <User size={16} weight="duotone" />
        </div>
      )}
    </div>
  );
}

export default function AdminChatSession() {
  const { sessionId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const hasToken = !!getAdminToken();

  const fetchSession = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await adminClient().get(`/admin/chat-sessions/${sessionId}`);
      setData(data);
    } catch (err) {
      setError(
        err?.response?.status === 404
          ? "Session not found."
          : err?.response?.data?.detail || "Could not load conversation."
      );
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (hasToken) fetchSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, hasToken]);

  if (!hasToken) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Helmet>
        <title>Chat transcript · TechConnect Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="border-b border-white/10 bg-zinc-900 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
          <Link
            to="/admin"
            data-testid="transcript-back"
            className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={16} weight="bold" />
            Back to dashboard
          </Link>
          <button
            onClick={fetchSession}
            disabled={busy}
            data-testid="transcript-refresh"
            className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-zinc-900 hover:bg-zinc-800 px-3 py-1.5 text-sm transition-colors"
          >
            <ArrowsClockwise size={14} weight="bold" className={busy ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-xl bg-blue-600/20 grid place-items-center text-blue-300">
            <ChatCircleDots size={20} weight="duotone" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold">Chat conversation</h1>
            <div className="text-xs text-zinc-500 font-mono mt-0.5">{sessionId}</div>
          </div>
        </div>

        {busy && !data && (
          <div className="flex items-center gap-2 text-zinc-400">
            <Spinner size={18} className="animate-spin" /> Loading transcript…
          </div>
        )}

        {error && (
          <div
            data-testid="transcript-error"
            className="rounded-xl bg-rose-500/15 border border-rose-500/30 text-rose-200 px-4 py-3"
          >
            {error}
          </div>
        )}

        {data && (
          <>
            {/* Lead-captured banner */}
            {data.leadCaptured && data.lead && (
              <div
                data-testid="transcript-lead-card"
                className="mb-6 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-5"
              >
                <div className="flex items-center gap-2 text-emerald-300 font-semibold">
                  <CheckCircle size={18} weight="fill" /> Lead captured during chat
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-emerald-400/70">Name</div>
                    <div className="text-white">{data.lead.name || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-emerald-400/70">Service</div>
                    <div className="text-white">{data.lead.service || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-emerald-400/70">Phone</div>
                    <a
                      href={`tel:${(data.lead.phone || "").replace(/\D/g, "")}`}
                      className="text-emerald-200 hover:text-white inline-flex items-center gap-1"
                    >
                      <Phone size={12} weight="bold" /> {data.lead.phone || "—"}
                    </a>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-emerald-400/70">Email</div>
                    <a
                      href={`mailto:${data.lead.email}`}
                      className="text-emerald-200 hover:text-white inline-flex items-center gap-1 truncate"
                    >
                      <EnvelopeSimple size={12} weight="bold" /> {data.lead.email || "—"}
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Meta */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 text-sm">
              <div className="bg-zinc-900 border border-white/10 rounded-xl p-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Messages</div>
                <div className="mt-1 font-semibold">{data.messages.length}</div>
              </div>
              <div className="bg-zinc-900 border border-white/10 rounded-xl p-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500">User turns</div>
                <div className="mt-1 font-semibold">
                  {data.messages.filter((m) => m.role === "user").length}
                </div>
              </div>
              <div className="bg-zinc-900 border border-white/10 rounded-xl p-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Started</div>
                <div className="mt-1 text-xs">{fmtDate(data.firstAt)}</div>
              </div>
              <div className="bg-zinc-900 border border-white/10 rounded-xl p-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Last activity</div>
                <div className="mt-1 text-xs">{fmtDate(data.lastAt)}</div>
              </div>
            </div>

            {/* Transcript */}
            <div
              data-testid="transcript-messages"
              className="bg-zinc-900/40 border border-white/10 rounded-2xl p-6 space-y-4"
            >
              {data.messages.map((m, i) => (
                <Bubble
                  key={i}
                  role={m.role}
                  text={m.text}
                  ts={m.createdAt}
                  suggestBooking={m.suggestBooking}
                />
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
